import { CTASection } from '../components/CTASection';
import { PageHero } from '../components/PageHero';
import { Section, SectionHeading, Reveal } from '../components/Section';
import { Icon } from '../lib/icons';
import { SEO } from '../lib/seo';

const values = [
  {
    title: 'Engineering first',
    body: 'Recommendations are shaped by maintainability, operational risk, supportability, and the business outcome.'
  },
  {
    title: 'Documentation matters',
    body: 'A system is not finished if no one can explain where it runs, how it works, who owns it, and what to do when it fails.'
  },
  {
    title: 'Practical before flashy',
    body: 'The best solution may be a clean internal tool, a reliable workflow, a documented network, or a better support model.'
  },
  {
    title: 'Long-term relationships',
    body: 'MaytoMation is built for organizations that want a technology partner who can still support the system years later.'
  }
];

export function About() {
  return (
    <>
      <SEO
        title="About"
        description="MaytoMation is a Massachusetts-based technology company focused on practical software, automation, infrastructure, documentation, and long-term support."
      />
      <PageHero
        eyebrow="About MaytoMation"
        title="A local engineering company for businesses that need technology to keep working."
        body="MaytoMation is based in Massachusetts and built around a simple belief: software, automation, infrastructure, and AI systems should solve real business problems and remain understandable after launch."
        primary={{ label: 'Book Consultation', href: '/booking' }}
        secondary={{ label: 'Explore Services', href: '/services' }}
        highlights={['Massachusetts based', 'Engineering-first', 'Documentation-focused', 'Long-term support']}
      />

      <Section>
        <div className="grid gap-10 lg:grid-cols-[.85fr_1.15fr] lg:items-start">
          <Reveal>
            <SectionHeading
              eyebrow="The Story"
              title="Built for owners, operators, property teams, contractors, and organizations with real technical work to do."
              body="MaytoMation exists for the gap between generic IT support, one-off development shops, and vendors that disappear after delivery."
            />
          </Reveal>
          <Reveal>
            <div className="rounded-lg border border-brand-border bg-white p-7 shadow-card">
              <div className="prose-brand max-w-none">
                <p>
                  Many businesses do not need a giant transformation program. They need someone who can understand how
                  work actually moves, build the missing system, clean up the fragile infrastructure, document what was
                  done, and stay available when the business changes.
                </p>
                <p>
                  MaytoMation approaches technology as operational infrastructure. A quoting tool should reflect real
                  pricing rules. A network closet should be labeled. A hosting environment should have backups,
                  monitoring, and a recovery plan. An AI assistant should use approved information and clear review
                  points. A dashboard should help people make decisions, not decorate a meeting.
                </p>
                <p>
                  The company is intentionally practical: local Massachusetts context, engineering depth, clear
                  communication, and long-term support options for clients who want systems they can rely on.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section className="bg-white">
        <SectionHeading
          eyebrow="Operating Values"
          title="The habits behind reliable systems."
          body="These values influence project scope, technical decisions, documentation, hosting, support, and client communication."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2">
          {values.map((value) => (
            <Reveal key={value.title}>
              <article className="rounded-lg border border-brand-border bg-brand-background p-6 shadow-card">
                <Icon name="check" className="h-7 w-7 text-brand-teal" />
                <h2 className="mt-5 text-xl font-extrabold text-brand-dark">{value.title}</h2>
                <p className="mt-3 text-sm leading-7 text-brand-muted">{value.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <div className="rounded-lg border border-brand-border bg-brand-dark p-8 text-white shadow-soft sm:p-10">
          <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div>
              <p className="text-sm font-extrabold uppercase tracking-[0.14em] text-brand-teal">Local + Supportable</p>
              <h2 className="mt-3 font-display text-4xl leading-tight">Technology work with an owner mindset.</h2>
            </div>
            <p className="text-lg leading-8 text-white/75">
              MaytoMation is a fit when you want a system built with care, explained clearly, and supported by someone
              who understands that uptime, documentation, handoff, and trust all matter.
            </p>
          </div>
        </div>
      </Section>

      <CTASection title="Looking for a practical engineering partner in Massachusetts?" button="Start a Conversation" />
    </>
  );
}
